import { Controller } from "@hotwired/stimulus"

type ModelLite = {
  id: string
  slug: string
  display_name: string
}

// Owns the "models in this set" chip strip + hidden inputs that submit with
// the surrounding model-set form. Cooperates with `model-picker` (mounted on
// the same element) by listening for the `model-picker:selected` custom event
// and by mirroring the current selection into the picker's value attributes
// so the next open seeds correctly.
export default class extends Controller {
  static targets = ["strip", "hiddenInputs", "modeSelect", "summary", "openButton"]
  static values = {
    fetchUrlBase: String, // e.g. /admin/playground_model_sets/picker_data
    setId: String,
    competitionTypeByMode: Object // { "text-to-image": "text_to_image", ... }
  }

  declare stripTarget: HTMLElement
  declare hiddenInputsTarget: HTMLElement
  declare modeSelectTarget: HTMLSelectElement
  declare summaryTarget: HTMLElement
  declare openButtonTarget: HTMLButtonElement
  declare fetchUrlBaseValue: string
  declare setIdValue: string
  declare competitionTypeByModeValue: Record<string, string>

  private models: ModelLite[] = []

  connect() {
    try {
      this.models = JSON.parse(this.element.getAttribute("data-initial-models") || "[]")
    } catch {
      this.models = []
    }
    this.syncPickerValues()
    this.render()
  }

  onModeChange() {
    // Switching modes invalidates the current selection (slugs may not be
    // compatible with the new mode's models). Wipe and let the admin re-pick.
    this.models = []
    this.syncPickerValues()
    this.render()
  }

  onPickerSelected(event: CustomEvent<{ ids: string[]; slugs: string[]; models: ModelLite[] }>) {
    const next = (event.detail.models || []).filter(m => m && m.slug)
    this.models = next
    this.syncPickerValues()
    this.render()
  }

  removeSlug(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const slug = button.dataset.slug
    if (!slug) return
    this.models = this.models.filter(m => m.slug !== slug)
    this.syncPickerValues()
    this.render()
  }

  private syncPickerValues() {
    const mode = this.modeSelectTarget.value
    const url = this.setIdValue
      ? `${this.fetchUrlBaseValue}?id=${encodeURIComponent(this.setIdValue)}&mode=${encodeURIComponent(mode)}`
      : `${this.fetchUrlBaseValue}?mode=${encodeURIComponent(mode)}`

    // model-picker is mounted on the same element as this controller, so
    // updating attributes here is what triggers the picker's value-changed
    // callbacks via Stimulus' MutationObserver.
    const root = this.element as HTMLElement
    root.setAttribute("data-model-picker-mode-value", mode)
    root.setAttribute("data-model-picker-fetch-url-value", url)
    root.setAttribute("data-model-picker-selected-ids-value",
      JSON.stringify(this.models.map(m => m.id)))

    const competitionType = this.competitionTypeByModeValue[mode] || ""
    const ssUrl = competitionType ? `/admin/arena_defaults/${competitionType}.json` : ""
    root.setAttribute("data-model-picker-save-standard-set-url-value", ssUrl)
  }

  private render() {
    if (this.models.length === 0) {
      this.stripTarget.innerHTML = `<p class="text-sm text-base-content/60 italic">No models selected yet.</p>`
    } else {
      this.stripTarget.innerHTML = this.models.map(m => `
        <span class="badge badge-outline gap-2 py-3">
          <span class="font-medium">${escapeHtml(m.display_name)}</span>
          <button type="button" class="opacity-60 hover:opacity-100"
                  data-action="click->model-set-roster#removeSlug"
                  data-slug="${escapeHtml(m.slug)}"
                  aria-label="Remove ${escapeHtml(m.display_name)}">×</button>
        </span>
      `).join("")
    }

    this.summaryTarget.textContent =
      this.models.length === 1 ? "1 model selected" : `${this.models.length} models selected`

    this.hiddenInputsTarget.innerHTML = this.models.map(m =>
      `<input type="hidden" name="playground_model_set[model_slugs][]" value="${escapeHtml(m.slug)}">`
    ).join("")
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!)
  )
}
